import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Info, AlertCircle, Mail, Download } from "lucide-react";
import { Layout } from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { EmailSdltModal } from "../components/EmailSdltModal";
import { printSdltEstimate } from "../lib/printSdlt";
import { usePageMeta } from "../hooks/usePageMeta";
import { CINEMATIC } from "../data/site";

// Residential SDLT bands — England, 1 April 2025 onwards
const STANDARD_BANDS = [
  { upTo: 125000, rate: 0 },
  { upTo: 250000, rate: 0.02 },
  { upTo: 925000, rate: 0.05 },
  { upTo: 1500000, rate: 0.1 },
  { upTo: Infinity, rate: 0.12 },
];

const FTB_BANDS = [
  { upTo: 300000, rate: 0 },
  { upTo: 500000, rate: 0.05 },
];

const SURCHARGE = 0.05;
const FTB_PRICE_CAP = 500000;

const fmt = (n) => `£${Math.round(n).toLocaleString("en-GB")}`;

const bandLabel = (from, to) =>
  to === Infinity ? `Above ${fmt(from)}` : `${fmt(from)} – ${fmt(to)}`;

const computeSdlt = (price, useFtb, additional) => {
  const bands = useFtb ? FTB_BANDS : STANDARD_BANDS;
  const rows = [];
  let total = 0;
  let prev = 0;
  for (const b of bands) {
    if (price <= prev) break;
    const top = Math.min(price, b.upTo);
    const taxable = top - prev;
    const baseRate = b.rate;
    const effectiveRate = additional ? baseRate + SURCHARGE : baseRate;
    const tax = taxable * effectiveRate;
    rows.push({
      from: prev,
      to: b.upTo === Infinity ? Infinity : top,
      baseRate,
      surcharge: additional ? SURCHARGE : 0,
      tax,
    });
    total += tax;
    prev = b.upTo;
  }
  return { rows, total };
};

const YesNo = ({ label, value, onChange, testid, help }) => (
  <div>
    <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-forest mb-2.5">{label}</p>
    <div className="flex border-2 border-forest bg-white overflow-hidden">
      {["yes", "no"].map((opt, i) => (
        <button
          key={opt}
          type="button"
          data-testid={`${testid}-${opt}`}
          onClick={() => onChange(opt)}
          aria-pressed={value === opt}
          className={`flex-1 min-h-[48px] text-[13px] font-bold tracking-[0.14em] uppercase transition-colors duration-300 ${
            i === 0 ? "border-r-2 border-forest" : ""
          } ${
            value === opt
              ? "bg-forest text-cream"
              : "bg-white text-forest hover:bg-cream-2"
          }`}
        >
          {opt === "yes" ? "Yes" : "No"}
        </button>
      ))}
    </div>
    {help && <p className="text-[12px] text-sage mt-2 leading-relaxed">{help}</p>}
  </div>
);

const StampDuty = () => {
  usePageMeta(
    "Stamp Duty Calculator | Elin's Consultancy",
    "Calculate Stamp Duty Land Tax (SDLT) for residential property in England — standard, first-time buyer and additional property (5% surcharge) rates, with a band-by-band breakdown matching HMRC."
  );

  const [priceStr, setPriceStr] = useState("");
  const [additional, setAdditional] = useState(null); // "yes" | "no"
  const [joint, setJoint] = useState(null); // "yes" | "no"
  const [ftb, setFtb] = useState(null); // when joint === "no": "yes" | "no"
  const [allFtb, setAllFtb] = useState(null); // when joint === "yes": "yes" | "no"
  const [emailOpen, setEmailOpen] = useState(false);

  const price = Number(priceStr.replace(/[^\d]/g, "")) || 0;

  // Flow state ------------------------------------------------------------
  const isAdditional = additional === "yes";
  const buyerClaimsFtb =
    joint === "yes" ? allFtb === "yes" : joint === "no" ? ftb === "yes" : false;
  const ftbQuestionAnswered =
    joint === "yes" ? allFtb !== null : joint === "no" ? ftb !== null : false;
  const allAnswered =
    price > 0 && additional !== null && joint !== null && ftbQuestionAnswered;

  // Relief eligibility — HMRC rules
  const reliefEligible =
    buyerClaimsFtb && !isAdditional && price > 0 && price <= FTB_PRICE_CAP;

  // Why relief was NOT applied, in order of priority (only shown when a buyer
  // claims FTB status that would otherwise qualify them)
  let reliefLostReason = null;
  if (buyerClaimsFtb) {
    if (isAdditional) reliefLostReason = "additional";
    else if (price > FTB_PRICE_CAP) reliefLostReason = "price_cap";
  } else if (joint === "yes" && allFtb === "no") {
    // mixed joint — explain even though no one in the group claimed FTB status
    reliefLostReason = "mixed_joint";
  }

  const result = useMemo(() => {
    if (!allAnswered) return null;
    return computeSdlt(price, reliefEligible, isAdditional);
  }, [allAnswered, price, reliefEligible, isAdditional]);

  const statusText = result
    ? isAdditional
      ? reliefEligible
        ? "First‑time buyer relief + 5% additional property surcharge applied"
        : "Standard rates + 5% additional property surcharge applied on every band"
      : reliefEligible
        ? "First‑time buyer relief applied"
        : "Standard residential rates applied"
    : "";

  const estimatePayload = useMemo(() => {
    if (!result) return null;
    return {
      price,
      total: result.total,
      statusText,
      bands: result.rows.map((r) => ({
        label: bandLabel(r.from, r.to),
        rate_label:
          r.surcharge > 0
            ? `${(r.baseRate * 100).toFixed(0)}% + ${(r.surcharge * 100).toFixed(0)}%`
            : `${(r.baseRate * 100).toFixed(0)}%`,
        tax: r.tax,
      })),
      additional: isAdditional,
      firstTimeBuyer: reliefEligible,
    };
  }, [result, price, statusText, isAdditional, reliefEligible]);

  const onPrice = (e) => {
    const digits = e.target.value.replace(/[^\d]/g, "").slice(0, 10);
    setPriceStr(digits ? Number(digits).toLocaleString("en-GB") : "");
  };

  const setJointAnswer = (v) => {
    setJoint(v);
    // reset dependent branch when the user flips joint
    if (v === "yes") setFtb(null);
    else setAllFtb(null);
  };

  // Idle hint text in the result card when calculator isn't yet complete
  const idleHint =
    price <= 0
      ? "Enter the property purchase price to get started."
      : additional === null
        ? "Tell us whether this will be an additional property."
        : joint === null
          ? "Tell us whether this is a joint purchase."
          : "Answer the first‑time buyer question to see your breakdown.";

  return (
    <Layout>
      <PageHero
        eyebrow="Stamp Duty Calculator"
        lines={[
          "Stamp Duty,",
          <>
            <em className="italic font-normal text-gold-soft">Calculated Clearly</em>
          </>,
        ]}
        text="Work out the Stamp Duty Land Tax on a residential purchase in England — HMRC rates for 1 April 2025, with first‑time buyer relief and the 5% additional‑property surcharge handled automatically."
        image={CINEMATIC.archColumns}
        imageAlt="Elegant fluted columns and decorative stonework on a period facade"
      />

      <section data-testid="sdlt-calculator" className="bg-cream">
        <div className="wrap-x py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-start">
          <Reveal>
            <div className="bg-cream-3 border border-forest/10 p-7 sm:p-10 space-y-8">
              {/* 1 — Price */}
              <div>
                <label
                  htmlFor="sdlt-price"
                  className="block text-[11px] tracking-[0.2em] uppercase font-bold text-forest mb-2.5"
                >
                  Property Purchase Price
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-forest/60 font-serif text-xl">£</span>
                  <input
                    id="sdlt-price"
                    data-testid="sdlt-price-input"
                    inputMode="numeric"
                    value={priceStr}
                    onChange={onPrice}
                    placeholder="350,000"
                    className="w-full bg-white border-2 border-forest/30 pl-10 pr-4 py-4 text-lg text-ink font-medium focus:border-gold focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* 2 — Additional property */}
              <YesNo
                label="Will this be an additional property?"
                testid="sdlt-additional"
                value={additional}
                onChange={setAdditional}
                help="Buy‑to‑lets, second homes and purchases where you'll own more than one property at completion attract a 5% surcharge on every band."
              />

              {/* 3 — Joint purchase */}
              <YesNo
                label="Is this a joint purchase?"
                testid="sdlt-joint"
                value={joint}
                onChange={setJointAnswer}
              />

              {/* 4a — Joint: all applicants FTB? */}
              {joint === "yes" && (
                <YesNo
                  label="Are ALL applicants first-time buyers?"
                  testid="sdlt-allftb"
                  value={allFtb}
                  onChange={setAllFtb}
                  help="For a joint purchase, every buyer must have never owned a property anywhere in the world — otherwise first‑time buyer relief doesn't apply."
                />
              )}

              {/* 4b — Solo: are you a FTB? */}
              {joint === "no" && (
                <YesNo
                  label="Are you a first-time buyer?"
                  testid="sdlt-ftb"
                  value={ftb}
                  onChange={setFtb}
                />
              )}

              {/* Explanation box when relief could not be applied */}
              {allAnswered && reliefLostReason === "mixed_joint" && (
                <div
                  data-testid="sdlt-relief-note"
                  className="flex gap-4 bg-gold/10 border border-gold/40 p-5"
                >
                  <AlertCircle size={20} strokeWidth={1.6} className="text-gold-dark shrink-0 mt-0.5" />
                  <p className="text-[13px] leading-[1.7] text-ink/80">
                    Because at least one applicant has owned a property before, first‑time buyer
                    relief <strong>cannot</strong> apply to this purchase — even for the first‑time
                    buyer in the group. Standard rates apply.
                  </p>
                </div>
              )}

              {allAnswered && reliefLostReason === "additional" && (
                <div
                  data-testid="sdlt-relief-note"
                  className="flex gap-4 bg-gold/10 border border-gold/40 p-5"
                >
                  <AlertCircle size={20} strokeWidth={1.6} className="text-gold-dark shrink-0 mt-0.5" />
                  <p className="text-[13px] leading-[1.7] text-ink/80">
                    First‑time buyer relief <strong>doesn't apply</strong> where the purchase
                    triggers the additional‑property surcharge. Standard rates plus 5% on every band
                    are used.
                  </p>
                </div>
              )}

              {allAnswered && reliefLostReason === "price_cap" && (
                <div
                  data-testid="sdlt-relief-note"
                  className="flex gap-4 bg-gold/10 border border-gold/40 p-5"
                >
                  <AlertCircle size={20} strokeWidth={1.6} className="text-gold-dark shrink-0 mt-0.5" />
                  <p className="text-[13px] leading-[1.7] text-ink/80">
                    First‑time buyer relief only applies to purchases of <strong>£500,000 or
                    less</strong>. Above that cap, standard residential rates apply to the whole
                    purchase price.
                  </p>
                </div>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div
              data-testid="sdlt-results"
              className="bg-forest text-cream p-7 sm:p-10 relative overflow-hidden grain"
            >
              <div className="relative z-[2]">
                <p className="eyebrow text-gold-soft">Your Estimate</p>
                {!result ? (
                  <p className="text-cream/65 text-sm leading-relaxed mt-6 max-w-sm">
                    {idleHint}
                  </p>
                ) : (
                  <>
                    <div className="mt-6 mb-8">
                      <p
                        data-testid="sdlt-total"
                        className="font-serif text-5xl sm:text-6xl text-gold-soft leading-none"
                      >
                        {fmt(result.total)}
                      </p>
                      <p className="text-[11px] tracking-[0.22em] uppercase font-bold text-cream/60 mt-3">
                        Total Stamp Duty Payable
                      </p>
                      <p data-testid="sdlt-status" className="text-[12px] text-cream/60 mt-2">
                        {statusText}
                      </p>
                    </div>
                    <table className="w-full text-[13px]">
                      <thead>
                        <tr className="border-b border-gold/25 text-[10px] tracking-[0.2em] uppercase text-gold-soft/80">
                          <th className="text-left py-3 font-bold">Band</th>
                          <th className="text-right py-3 font-bold">Rate</th>
                          <th className="text-right py-3 font-bold">Tax Due</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.rows.map((r, i) => (
                          <tr
                            key={i}
                            data-testid={`sdlt-band-${i + 1}`}
                            className="border-b border-cream/10"
                          >
                            <td className="py-3.5 text-cream/85">{bandLabel(r.from, r.to)}</td>
                            <td className="py-3.5 text-right text-cream/85">
                              {r.surcharge > 0 ? (
                                <span>
                                  {(r.baseRate * 100).toFixed(0)}%
                                  <span className="text-gold-soft">
                                    {" "}
                                    +{(r.surcharge * 100).toFixed(0)}%
                                  </span>
                                </span>
                              ) : (
                                <>{(r.baseRate * 100).toFixed(0)}%</>
                              )}
                            </td>
                            <td className="py-3.5 text-right font-medium">{fmt(r.tax)}</td>
                          </tr>
                        ))}
                        <tr>
                          <td className="py-4 font-bold text-cream" colSpan={2}>
                            Total
                          </td>
                          <td className="py-4 text-right font-bold text-gold-soft">
                            {fmt(result.total)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </>
                )}

                <div className="mt-8 pt-6 border-t border-cream/15 flex gap-4">
                  <Info size={18} strokeWidth={1.6} className="text-gold-soft shrink-0 mt-0.5" />
                  <p className="text-[12px] leading-[1.7] text-cream/60">
                    Based on residential SDLT rates for England from 1 April 2025. An additional
                    5% surcharge applies on every band for buy‑to‑lets, second homes and most
                    purchases where you'll own more than one property on completion; non‑UK
                    residents pay a further 2%. This calculator is a guide, not tax advice —{" "}
                    <Link
                      to="/contact"
                      data-testid="sdlt-advice-link"
                      className="text-gold-soft underline underline-offset-4 hover:text-gold transition-colors"
                    >
                      speak to us
                    </Link>{" "}
                    for advice on your full position.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
              <button
                type="button"
                data-testid="sdlt-email-button"
                onClick={() => setEmailOpen(true)}
                disabled={!estimatePayload}
                className="btn-ghost-dark justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                title={
                  estimatePayload
                    ? "Email a copy of this estimate"
                    : "Enter a price to generate an estimate first"
                }
              >
                <Mail size={14} strokeWidth={2} /> Email This Estimate
              </button>
              <button
                type="button"
                data-testid="sdlt-pdf-button"
                onClick={() => estimatePayload && printSdltEstimate(estimatePayload)}
                disabled={!estimatePayload}
                className="btn-ghost-dark justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                title={
                  estimatePayload
                    ? "Download or print a tidy one-page estimate"
                    : "Enter a price to generate an estimate first"
                }
              >
                <Download size={14} strokeWidth={2} /> Download PDF
              </button>
              <Link
                to="/contact"
                data-testid="sdlt-contact-button"
                className="btn-forest justify-center"
              >
                Discuss Your Purchase <ArrowRight size={14} strokeWidth={2.2} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <EmailSdltModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        estimate={estimatePayload}
      />
    </Layout>
  );
};

export default StampDuty;
